import React, { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Member, Team, TeamMember } from '../../../../../../types/entity.types';
import { RadioOrSelectOption } from '../../../../../../types/common.types';

import Modal from '../../../../../common/modal/Modal';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Dropdown from '../../../../../common/input/dropdown/Dropdown';
import Button from '../../../../../common/button/Button';

import { ReactComponent as DeleteIcon } from '../../../../../../resources/icons/close.svg';

import '../team-modals.scss';

type Props = {
  show: boolean;
  close: () => void;
  members: Member[];
  createTeam: (teamToSave: Team, close: () => void) => void;
};

const AddTeamModal = ({ show, close, members, createTeam }: Props): ReactElement => {
  const [name, setName] = useState<string>('');
  const [selectedMembers, setSelectedMembers] = useState<TeamMember[]>([]);

  const { programmeExternalReference } = useParams();

  useEffect((): void => {
    if (!show) {
      setName('');
      setSelectedMembers([]);
    }
  }, [show]);

  const availableMembers: RadioOrSelectOption[] = useMemo((): RadioOrSelectOption[] => {
    const filteredMembers: Member[] = members.filter(
      (member: Member): boolean =>
        !selectedMembers.some(
          ({ externalReference }: TeamMember): boolean => externalReference === member.externalReference
        )
    );

    const sortedMembers: Member[] = filteredMembers.sort((memberOne: Member, memberTwo: Member): number => {
      if (!memberOne.name || !memberTwo.name) return 0;
      if (memberOne.name > memberTwo.name) return 1;
      if (memberOne.name < memberTwo.name) return -1;
      return 0;
    });

    return sortedMembers.map(
      (member: Member): RadioOrSelectOption => ({
        label: member.name!,
        value: member.memberExternalReference!
      })
    );
  }, [members, selectedMembers]);

  if (!show) return <React.Fragment />;

  const handleDeleteMemberClick = (member: TeamMember): void => {
    setSelectedMembers((prev: TeamMember[]): TeamMember[] =>
      prev.filter(({ externalReference }: TeamMember): boolean => externalReference !== member.externalReference)
    );
  };

  const handleSaveClick = (): void => {
    const teamToSave: Team = {
      teamName: name,
      memberExternalReferences: selectedMembers.map(
        ({ externalReference }: TeamMember): string => externalReference as string
      ),
      programmeExternalReference: programmeExternalReference
    };

    createTeam(teamToSave, close);
  };

  const renderMemberList = (): ReactElement => {
    if (selectedMembers.length === 0) return <React.Fragment />;

    return (
      <section className="add-team-modal__member-list visible-scrollbar">
        {selectedMembers.map(
          (member: TeamMember): ReactElement => (
            <div className="add-team-modal__selected-member" key={member.externalReference}>
              <p>{member.name}</p>
              <DeleteIcon onClick={(): void => handleDeleteMemberClick(member)} data-testid="remove-member-button" />
            </div>
          )
        )}
      </section>
    );
  };

  const handleDropdownChange = (option: RadioOrSelectOption): void => {
    const selectedOption: RadioOrSelectOption = option;
    const member: TeamMember = {
      name: selectedOption.label,
      externalReference: selectedOption.value
    };
    setSelectedMembers((prev: TeamMember[]): TeamMember[] => [...prev, member]);
  };

  return (
    <Modal show={show}>
      <section className="team-modals">
        <h1 className="team-modals__header">Add Team</h1>
        <section className="add-team-modal__inputs">
          <TextInput
            label="Team Name:"
            name="name"
            value={name}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setName(target.value)}
            autoFocus
          />

          <section className="add-team-modal__member-dropdown-container">
            <label className="add-team-modal__member-dropdown-label" htmlFor="members">
              Team Members:
            </label>
            <div className="add-team-modal__member-dropdown">
              {renderMemberList()}
              <Dropdown name="members" options={availableMembers} onChange={handleDropdownChange} />
            </div>
          </section>
        </section>
        <section className="team-modals__button-container">
          <Button text="Cancel" onClick={close} />
          <Button text="Save" onClick={handleSaveClick} disabled={!name} />
        </section>
      </section>
    </Modal>
  );
};

export default AddTeamModal;
